import assert from "assert";
import mudder from "mudder";
import { getChildren } from "./reactive-cache.js";

const DEBUG = true;

export const seqSpace = mudder.base62;
// Ends of the (open) range of the seq values to use.
const kMinSeq = seqSpace.num2sym[0];
const kMidSeq = seqSpace.mudder()[0];
const kMaxSeq = seqSpace.num2sym[seqSpace.num2sym.length - 1];

function debugItems(header, objects, relation) {
  for (const obj of objects) {
    const name = (obj.name || "").slice(0, 30);
    const pClass = obj[relation]?.parentClass;
    const pKey = obj[relation]?.parentKey;
    const seq = obj[relation]?.seq;
    console.debug(`${header}: ${obj._id} "${name}" -> ${pKey} (${pClass}) ${seq}`);
  }
}

export function prepareReorder({
  reactiveCache, relation, itemOrItems, newPPtr, insertPos = null, skipOrdered = true,
}) {
  const items =
    Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];

  const children = getChildren({
    cache: reactiveCache,
    relation: relation,
    pClass: newPPtr.parentClass,
    pKey: newPPtr.parentKey,
  });

  if (insertPos === null) {
    insertPos = children.length;
  }
  assert(insertPos >= 0, `prepareReorder: bad pos: ${insertPos}`);
  assert(insertPos <= children.length, `prepareReorder: bad pos: ${insertPos}`);

  if (DEBUG) {
    console.group(`prepareReorder (${relation}):`);
    debugItems("before", items, relation);

    const seqs = children.map((obj) => obj[relation].seq);
    console.debug(`New parent: ${newPPtr.parentKey} (${newPPtr.parentClass})`);
    console.debug(`Sibling seqs: ${seqs}`);
    console.debug(
      `InsertPos: ${insertPos} - between (${seqs[insertPos - 1]}, ${seqs[insertPos]})`
    );
    performance.mark("begin");
  }

  const updatedItems = updateSeqs(
    newPPtr, relation, children, items, insertPos, skipOrdered
  );

  if (DEBUG) {
    performance.mark("end");

    if (updatedItems.length) {
      debugItems("after", updatedItems, relation);
    } else {
      console.debug("Nothing to do.");
    }
    const m = performance.measure("prepareReorder", "begin", "end");
    console.debug(`time: ${m.duration.toFixed(2)} ms`);
    console.groupEnd();
  }

  return updatedItems;
}

// insertObjs should be an array of objects, in the order they will be inserted.
// In general case not only the "input" items may be modified - some neighbors
// may need updated seqs. However, in case of putting items at the beginning/end,
// this should not happen.
function updateSeqs(newPPtr, relation, children, insertObjs, insertPos, skipOrdered) {
  if (insertObjs.length === 0) {
    return [];
  }

  // Check for the special case when nothing is actually changing.
  let nextObjIdx = 0;
  for (let i = 0; i < children.length; ++i) {
    if (children[i]._id === insertObjs[nextObjIdx]._id) {
      ++nextObjIdx;
      if (nextObjIdx == insertObjs.length) {
        // We have found all the insertObjs packed consecutively inside children
        // array.
        if (insertPos > i - insertObjs.length && insertPos <= i + 1) {
          // The insertPos is such that insertObjs won't be moved, so there is
          // nothing happening.
          if (skipOrdered) { return []; } else { break; }
        }
        // There will be some change, nothing more to find in this loop.
        break;
      }
    } else if (nextObjIdx > 0) {
      // The chain of matching objects was broken, so there must be an actual
      // change.
      break;
    }
  }

  // Create modified copies of insertObjs, though with seq not set yet.
  const modifiedObjs = insertObjs.map((obj) =>
    obj.clone({
      [relation]: {
        parentClass: newPPtr.parentClass,
        parentKey: newPPtr.parentKey,
      },
      [relation + '-reorder-count']: ++obj[relation + '-reorder-count'] || 0
    })
  );

  // "Edge" case: insert at the beginning/end of the given container.
  if (insertPos === 0 || insertPos === children.length) {
    // Don't distribute seqs evenly - leave more space near the ends
    // to accomodate future appends/prepends.
    // When inserting into an empty container, assign seqs just before
    // the middle of the available space. Otherwise assign seqs just before
    // the first / just after the last existing child.
    const seqs = children.map((obj) => obj[relation].seq);
    const startSeq = insertPos === 0 ? kMinSeq : seqs[seqs.length - 1];
    seqs.push(kMaxSeq);
    const endSeq = children.length ? seqs[insertPos] : kMidSeq

    const divisions = insertObjs.length + seqSpace.num2sym.length;
    const newSeqs = seqSpace.mudder(startSeq, endSeq, divisions)
    newSeqs.splice(insertPos ? insertObjs.length : 0, seqSpace.num2sym.length)

    for (let i = 0; i < modifiedObjs.length; i++) {
      modifiedObjs[i][relation].seq = newSeqs[i];
    }
    return modifiedObjs;
  }

  // Insert between other objects.
  const insertIds = new Set(insertObjs.map((obj) => obj._id));
  // Decrese the insertPos to be correct after filtering out insertObjs.
  const objsBeforeInsertPos = insertPos - 1;
  for (let i = 0; i < objsBeforeInsertPos; ++i) {
    if (insertIds.has(children[i]._id)) {
      --insertPos;
    }
  }
  // Filter insertObjs out of children list.
  // It spares us some tricky corner cases.
  children = children.filter((obj) => !insertIds.has(obj._id));

  // Figure out the ordering.
  const seqs = children.map((obj) => obj[relation].seq);
  const prevSeq = seqs[insertPos - 1];
  const nextSeq = seqs[insertPos];

  // Simple case - no duplicated seqs present.
  if (prevSeq != nextSeq) {
    const insertSeqs = seqSpace.mudder(prevSeq, nextSeq, modifiedObjs.length);
    for (let i = 0; i < modifiedObjs.length; ++i) {
      modifiedObjs[i][relation].seq = insertSeqs[i];
    }
    // TODO: Filter out the objects that were not changed?
    return modifiedObjs;
  }

  // Handle duplicated seq.
  // Note: kMinSeq and kMaxSeq are never duplicated, as they are sentinel values
  // and should never be assigned to actual objects.
  const dupedSeq = prevSeq;
  const firstDupIdx = seqs.indexOf(dupedSeq);
  const lastDupIdx = seqs.lastIndexOf(dupedSeq);
  const newSeqsCount = lastDupIdx - firstDupIdx + 1 + modifiedObjs.length;
  const newSeqs =
    seqSpace.mudder(seqs[firstDupIdx - 1], seqs[lastDupIdx + 1], newSeqsCount);
  for (let i = 0; i < modifiedObjs.length; ++i) {
    modifiedObjs[i][relation].seq = newSeqs[insertPos - firstDupIdx + i];
  }

  // Update seqs in objects before the ones inserted.
  for (let i = firstDupIdx; i < insertPos; ++i) {
    const obj = children[i].clone();
    obj[relation].seq = newSeqs[i - firstDupIdx];
    modifiedObjs.push(obj);
  }

  // Update seqs in objects after the ones inserted.
  for (let i = insertPos; i <= lastDupIdx; ++i) {
    const obj = children[i].clone();
    obj[relation].seq = newSeqs[i - firstDupIdx + 1];
    modifiedObjs.push(obj);
  }

  // TODO: Filter out the objects that were not changed?
  return modifiedObjs;
}

export function getChildPos(cache, task, relation) {
  return getChildren({
    cache,
    relation,
    pClass: task[relation].parentClass,
    pKey: task[relation].parentKey,
  }).indexOf(task);
}

export function reorderTasks({
  relation, db, reactiveCache, task, tasks, newPPtr, insertPos = null, forceSave = false
}) {
  const updatedTasks = prepareReorder({
    reactiveCache,
    relation,
    itemOrItems: task || tasks,
    newPPtr,
    insertPos,
    forceSave,
  });
  if (db) {
    return db.bulkDocs(updatedTasks);
  } else {
    return Promise.all(updatedTasks.map((task) => task.save()));
  }
}

export function multiRelationAppend({
  db, reactiveCache, task, subtaskOf, relation, newPPtr,
}) {
  const withUpdatedSubtaskOf = prepareReorder({
    reactiveCache,
    relation: "subtaskOf",
    itemOrItems: task,
    newPPtr: subtaskOf,
    insertPos: null,
  });
  // when inserting tasks at the end, there should be no updates to other tasks,
  // so it should be safe to run another prepareReorder.
  const updatedTasks = prepareReorder({
    reactiveCache,
    relation,
    itemOrItems: withUpdatedSubtaskOf,
    newPPtr,
    insertPos: null,
  });
  if (db) {
    return db.bulkDocs(updatedTasks);
  } else {
    return Promise.all(updatedTasks.map((task) => task.save()));
  }
}

// Put a bunch of objects at the end of the given container.
export function prepareBulkReorderAtEnd({
  reactiveCache, objects, relation, newPPtr,
}) {
  const sectionContents = getChildren({
    cache: reactiveCache,
    relation: relation,
    pClass: newPPtr.parentClass,
    pKey: newPPtr.parentKey,
  });
  const prevSeq =
    sectionContents.length > 0
      ? sectionContents[sectionContents.length - 1][relation].seq
      : kMinSeq;
  const newSeqs = seqSpace.mudder(prevSeq, kMaxSeq, objects.length);
  return objects.map((task, idx) =>
    task.clone({
      [relation]: {
        parentClass: newPPtr.parentClass,
        parentKey: newPPtr.parentKey,
        seq: newSeqs[idx],
      },
      [relation + '-reorder-count']: ++task[relation + '-reorder-count'] || 0
    })
  );
}

export async function bulkReorderAtEnd({
  relation, reactiveCache, tasks, newPPtr
}) {
  const tasksToSave = prepareBulkReorderAtEnd({
    relation,
    reactiveCache,
    objects: tasks,
    newPPtr,
  });
  return Promise.all(tasksToSave.map((task) => task.save()));
}

// Reschedules multiple tasks at the end of the given day/week.
export async function bulkRescheduleAtEnd(options) {
  return bulkReorderAtEnd(Object.assign({}, options, { relation: "plannedFor" }));
}

export async function bulkNestAtEnd(options) {
  return bulkReorderAtEnd(Object.assign({}, options, { relation: "subtaskOf" }));
}

export async function bulkCategoryAtEnd(options) {
  return bulkReorderAtEnd(Object.assign({}, options, { relation: "category" }));
}

export async function bulkStatusAtEnd(options) {
  return bulkReorderAtEnd(Object.assign({}, options, { relation: "status" }));
}

export function removePlannedFor(taskToEdit) {
  return taskToEdit.clone({ delKey: "plannedFor" }).save();
}

export function withoutSubtaskOf(taskToEdit) {
  return taskToEdit.clone({ delKey: "subtaskOf" });
}

export function removeCategory(taskToEdit) {
  return taskToEdit.clone({ delKey: "category" }).save();
}

export function removeStatus(taskToEdit) {
  return taskToEdit.clone({ delKey: "status" }).save();
}

export function getAncestorIds(reactiveCache, taskId, includeSelf = false) {
  const result = [];
  let task = reactiveCache.docs[taskId];
  if (task && includeSelf) {
    result.push(taskId);
  }
  while (
    task &&
    task.subtaskOf &&
    (task = reactiveCache.docs[task.subtaskOf.parentKey])
  ) {
    result.push(task._id);
  }
  return result;
}

// Warning: in the current implementation the returned data references objects
// inside the reactive view. These should never be mutated.
export function getReversedAncestors(reactiveCache, task, includeList = false) {
  const result = [];
  const visited = new Set();
  const appendReversed = (t) => {
    if (!t || visited.has(t)) {
      return;
    }
    visited.add(t);

    const parentTask = t.subtaskOf && reactiveCache.docs[t.subtaskOf.parentKey];
    if (parentTask) {
      appendReversed(parentTask);
      result.push({ subtaskOf: t.subtaskOf });
    } else if (t.subtaskOf && includeList) {
      result.push({ subtaskOf: t.subtaskOf });
    }
  };
  appendReversed(task);
  return result;
}
